import React, { useEffect, useState } from "react";
import LightWeightCharts from "./LightWeightCharts";
import { useUserContext } from "@/contexts/userContext";
import { useGetTickerData } from "@/api/symbolApi";
import { ChartDataCustomTypesPerDataset } from "chart.js";
import { AccentColor } from "@/helpers/types";
import { prepareChartData } from "@/lib/utils";

const TradingGraph = ({
  symbol,
  timeframe,
  disableInteraction,
  canvas_data,
  broker_id,
}: {
  symbol: string;
  timeframe: string;
  disableInteraction?: boolean;
  canvas_data?: any;
  broker_id?: string;
}) => {
  const [isGraphLoading, setGraphLoading] = useState(true);
  const [graphDataNew, setGraphDataNew] = useState<
    ChartDataCustomTypesPerDataset[]
  >([]);
  const [volumeData, setVolumeData] = useState<any[]>([]);
  const [graphDataOtherDetails, setGraphDataOtherDetails] = useState({});
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [canvasData, setCanvasData] = useState<any>(
    canvas_data ?? JSON.stringify(bbandsNode)
  );
  const { mutateAsync: getTickerData } = useGetTickerData();
  const { user, connectedBrokers } = useUserContext();

  const loadTickerData = async (sym: string, tf: string) => {
    if (!sym || !tf || !user?.user_id) return;
    setGraphLoading(true);
    try {
      const res = await getTickerData({
        timestamp: tf,
        ticker: sym,
        user_id: user?.user_id ?? "",
        broker_id: broker_id,
      });
      if (res?.statusText === "OK") {
        const time = res?.data?.data?.time || [];
        const olhc = res?.data?.data?.olhc || {};
        const formattedGraph: any[] = [];
        const volumes: any[] = [];

        time.forEach((timestamp: number, idx: number) => {
          formattedGraph.push({
            time: timestamp,
            open: Number(olhc.open?.[idx]),
            high: Number(olhc.high?.[idx]),
            low: Number(olhc.low?.[idx]),
            close: Number(olhc.close?.[idx]),
            volume: Number(olhc.volume?.[idx]),
          });

          volumes.push({
            time: timestamp,
            value: Number(olhc.volume?.[idx]),
            color:
              Number(olhc.close?.[idx]) > Number(olhc.open?.[idx])
                ? AccentColor.GREEN
                : AccentColor.RED,
          });
        });
        setCurrentPrice(formattedGraph[formattedGraph.length - 1].close);
        setGraphDataNew(formattedGraph);
        setVolumeData(volumes);
        setGraphDataOtherDetails({ timestamp: tf, symbol: sym });
      }
    } catch (error) {
      console.error("Error fetching ticker data:", error);
    } finally {
      setGraphLoading(false);
    }
  };

  useEffect(() => {
    if (symbol && timeframe) {
      loadTickerData(symbol, timeframe);
      return () => setGraphLoading(true);
    }
  }, [symbol, timeframe, user, connectedBrokers]);
  return isGraphLoading ? (
    <div className="f-full h-full flex items-center justify-center"></div>
  ) : (
    <LightWeightCharts
      currentPrice={currentPrice}
      graphData={graphDataNew}
      volumeData={volumeData}
      canvasData={canvasData}
      graphDataOtherDetails={graphDataOtherDetails}
      enableIndicator={true}
    ></LightWeightCharts>
  );
};

export default TradingGraph;

const bbandsNode = [
  {
    id: "node_0.3757714123671704",
    type: "selectorNode",
    position: {
      x: 2120.27002557172,
      y: -274.8771620784567,
    },
    data: {
      inputs_list: ["input"],
      inputs: [
        {
          id: "input-port-0.560815535764849",
          name: "input",
        },
      ],
      outputs: [
        {
          id: "output-port-0.4115455779109405",
          name: "bbands_lower",
        },
        {
          id: "output-port-0.13737675821535755",
          name: "bbands_middle",
        },
        {
          id: "output-port-0.1477573437793709",
          name: "bbands_upper",
        },
        {
          id: "output-port-0.1019508973131742",
          name: "bandwidth",
        },
        {
          id: "output-port-0.8991274656276623",
          name: "percent",
        },
      ],
      outputs_list: [
        "bbands_lower",
        "bbands_middle",
        "bbands_upper",
        "bandwidth",
        "percent",
      ],
      other_inputs: [
        {
          type: "input",
          min_value: 1,
          max_value: 500,
          default_value: "2",
          name: "stddev",
          input_type: "Float",
          value: "2",
        },
        {
          type: "input",
          min_value: 2,
          max_value: 500,
          default_value: "20",
          name: "period",
          input_type: "Float",
          value: "20",
        },
        {
          type: "dropdown",
          min_value: 0,
          max_value: 500,
          name: "real",
          options: ["Open", "High", "Low", "Close"],
          input_type: "Float",
          value: "Close",
        },
      ],
      title: "BBands - Bollinger Bands",
      short_name: "bbands",
      category: "Volatility",
      description: "A popular volatility indicator by John Bollinger.\n",
      type: "Indicator",
      unique_key: "b29dedc489097d4cc4cbd6faac64b005",
      id: "node_0.3757714123671704",
      name: "BBands - Bollinger Bands",
    },
    measured: {
      width: 320,
      height: 511,
    },
  },
];
